import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { EnvelopeSimple, X, Paperclip } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";

export default function TemplatesPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [letters, setLetters] = useState([]);
  const [policies, setPolicies] = useState([]);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);

  const loadLetters = useCallback(() => api.get("/letters/templates").then((r) => setLetters(r.data)).catch(() => {}), []);

  useEffect(() => {
    loadLetters();
    if (isAdmin) api.get("/policies").then((r) => setPolicies(r.data || [])).catch(() => {});
  }, [loadLetters, isAdmin]);

  const saveLetter = async () => {
    if (!editing.title.trim() || !editing.body.trim()) { toast.error("Title and body are required"); return; }
    setBusy(true);
    try {
      const payload = { title: editing.title, body: editing.body, attached_policy_id: editing.attached_policy_id || "" };
      if (editing.id) await api.put(`/letters/templates/${editing.id}`, payload);
      else await api.post("/letters/templates", payload);
      toast.success(editing.attached_policy_id
        ? "Letter template saved — the attached policy will be assigned for sign-off when this letter is generated"
        : "Letter template saved");
      setEditing(null);
      loadLetters();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const deactivateLetter = async (t) => {
    try {
      await api.put(`/letters/templates/${t.id}`, { active: false });
      loadLetters();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const policyTitle = (pid) => (policies.find((p) => p.id === pid) || {}).title || pid;

  return (
    <div className="max-w-4xl" data-testid="templates-page">
      <h1 className="font-heading text-4xl font-semibold tracking-tight text-stone-900">Employee form letters</h1>
      <p className="mt-1 text-sm text-stone-500">
        Digitize office letters here, then generate them from an employee's page (Team → select employee).
        Looking for blank forms? They all live in the <Link to="/documents" className="font-medium text-primary hover:underline" data-testid="link-document-library">Document library</Link>.
      </p>

      {isAdmin && (
        <div className="mt-12" data-testid="letter-templates-section">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h2 className="font-heading text-lg font-semibold text-stone-900">Employee Form Letters</h2>
              <p className="mt-1 max-w-xl text-xs leading-relaxed text-stone-500">
                Merge fields available: {"{{employee_name}} {{employee_email}} {{employee_id}} {{start_date}} {{date}} {{sender_name}} {{details}}"}.
                Paste your existing letters here to digitize them. Generate letters from an employee's page (Team → select employee).
                You can attach a policy — when the letter is generated for an employee, that policy is assigned to them for read/quiz/sign-off.
              </p>
            </div>
            <Button onClick={() => setEditing({ title: "", body: "", attached_policy_id: "" })}
              className="bg-primary text-primary-foreground hover:bg-stone-900" data-testid="new-letter-template-button">
              + New Letter Template
            </Button>
          </div>
          <div className="mt-4 space-y-3">
            {letters.map((t) => (
              <div key={t.id} className="flex flex-wrap items-center gap-4 rounded-lg border border-stone-200 bg-white p-5 shadow-sm" data-testid={`letter-template-${t.id}`}>
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-accent text-accent-foreground">
                  <EnvelopeSimple size={22} />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="font-heading text-sm font-semibold text-stone-900">{t.title}</p>
                  <p className="mt-0.5 truncate text-xs text-stone-500">{t.body.split("\n")[0]}</p>
                  {t.attached_policy_id && (
                    <p className="mt-1 inline-flex items-center gap-1 rounded bg-accent px-1.5 py-0.5 text-[10px] font-semibold text-accent-foreground" data-testid={`letter-policy-${t.id}`}>
                      <Paperclip size={11} /> Policy attached: {policyTitle(t.attached_policy_id)}
                    </p>
                  )}
                </div>
                <button onClick={() => setEditing({ ...t, attached_policy_id: t.attached_policy_id || "" })} data-testid={`letter-edit-${t.id}`}
                  className="inline-flex h-9 items-center rounded-md border border-stone-300 px-3 text-sm font-medium text-stone-700 transition-colors hover:bg-stone-900 hover:text-white">
                  Edit
                </button>
                <button onClick={() => deactivateLetter(t)} data-testid={`letter-deactivate-${t.id}`}
                  className="inline-flex h-9 items-center rounded-md border border-stone-300 px-3 text-sm font-medium text-stone-500 transition-colors hover:text-[#C0392B]">
                  Retire
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="letter-editor-modal">
          <div className="max-h-[85vh] w-full max-w-lg overflow-y-auto rounded-lg bg-white p-6 shadow-xl">
            <div className="flex items-center justify-between">
              <h2 className="font-heading text-lg font-semibold text-stone-900">{editing.id ? "Edit Letter Template" : "New Letter Template"}</h2>
              <button onClick={() => setEditing(null)} className="text-stone-400 hover:text-stone-700" data-testid="letter-editor-close"><X size={20} /></button>
            </div>
            <div className="mt-4 space-y-4">
              <div className="space-y-1.5">
                <Label>Title</Label>
                <Input value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })}
                  className="border-stone-300 bg-white" data-testid="letter-title-input" placeholder="e.g. Notice of Non-Compliance — EVV" />
              </div>
              <div className="space-y-1.5">
                <Label>Letter body</Label>
                <Textarea rows={12} value={editing.body} onChange={(e) => setEditing({ ...editing, body: e.target.value })}
                  className="border-stone-300 bg-white text-sm" data-testid="letter-body-input"
                  placeholder="Dear {{employee_name}}, ..." />
                <p className="text-[11px] text-stone-400">{"{{details}}"} is filled in when the letter is generated — use it for the case-specific facts.</p>
              </div>
              <div className="space-y-1.5">
                <Label>Attach a policy (optional)</Label>
                <select value={editing.attached_policy_id || ""} onChange={(e) => setEditing({ ...editing, attached_policy_id: e.target.value })}
                  className="h-9 w-full rounded-md border border-stone-300 bg-white px-3 text-sm" data-testid="letter-policy-select">
                  <option value="">No policy attached</option>
                  {policies.filter((p) => p.status !== "archived").map((p) => (
                    <option key={p.id} value={p.id}>{p.title}{p.quiz_count ? ` (quiz: ${p.quiz_count})` : ""}</option>
                  ))}
                </select>
                <p className="text-[11px] text-stone-400">
                  When this letter is generated for an employee, the attached policy is assigned to them — they must read it, answer its quiz (if any), and sign the acknowledgment. The signed submission is the proof.
                </p>
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setEditing(null)} className="border-stone-300 text-stone-500" data-testid="letter-cancel">Cancel</Button>
                <Button onClick={saveLetter} disabled={busy} className="bg-primary text-primary-foreground hover:bg-stone-900" data-testid="letter-save">
                  {busy ? "Saving..." : "Save Template"}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
